import styled from "@emotion/styled";
import type { FC } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { mergeObjects } from "@rjsf/utils";
import { FormContainer } from "./FormContainer";
import Styles from "@/assets/styles/Styles";
import MediaSelector from "@/components/MediaSelector/MediaSelector";
import PersistentDrawer from "@/components/PersistentDrawer";
import type { Document } from "@/declarations/models/Document";
import { DocumentStatus } from "@/declarations/models/DocumentStatus";
import KioForm from "@/framework/KioForm/KioForm";
import Layout from "@/framework/Layout";
import { usePrompt } from "@/hooks/useUNSAFE_Prompt";
import Settings from "@/Settings";
import { useStore } from "@/Store";
import { deepCompareFormData, findDeepPropertiesWithContent, objectFromPath } from "@/utils/obj";
import type { EditDocumentLoaderProps, SchemaObject } from "@/views/cms/EditorView/EditDocumentLoader";
import EditorHeader from "@/views/cms/EditorView/EditorHeader";
import EditorPreview from "@/views/cms/EditorView/EditorPreview/EditorPreview";
import type { BlockClipboard, BlockClipboardContextValue } from "@/BlockClipboardContextProvider";
import { BlockClipboardContext } from "@/BlockClipboardContextProvider";
import { setForceUpdateLocalStorage } from "@/utils/customEvents";
import { createCompositeId, useIndexedDB } from "@/hooks/useIndexedDB";
import useUserGestureFlag from "@/hooks/useUserGestureFlag";
import AiFeedbackView from "@/components/AiFeedbackView";

interface EditDocumentViewProps extends EditDocumentLoaderProps {
  schemas: SchemaObject;
  documentToEdit?: Document | null;
  handleSaveDocument: any;
  selectedLocale: any;
  setSelectedLocale: any;
  setReferences: any;
}

const ErrorText = styled("div")`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 20px;
  font-weight: 700;
  color: crimson;
`;

export const EditDocumentView: FC<EditDocumentViewProps> = ({
  disablePreview,
  schemas,
  documentToEdit,
  handleSaveDocument,
  selectedLocale,
  setSelectedLocale,
  setReferences,
  modalEditor,
  isDirty,
  setIsDirty,
}) => {
  const { state, setRestoreDocumentStatus, setGeneratedData } = useStore();
  const [tempStorageDB, addDocumentToIndexedDB, getDocumentFromIndexedDB, deleteDocumentFromIndexedDB] = useIndexedDB(
    "tempStorage",
    "documents"
  );
  const availableLocales = (state.cmsContextInstance?.available_locales || []).map((al) => ({
    language_code: al.language_code!,
    id: al.id!,
  }));
  const { t } = useTranslation("common");
  const userHasDoneChanges = useUserGestureFlag();

  const [previewOpen, setPreviewOpen] = useState<boolean>(true);
  const [mediaSelectorOpen, setMediaSelectorOpen] = useState<boolean>(false);
  const [modifiedContent, setModifiedContent] = useState<any>(documentToEdit?.content || {});
  const [selectedDocumentStatus, setSelectedDocumentStatus] = useState<DocumentStatus>(
    documentToEdit?.status || DocumentStatus.DRAFT
  );
  const [restoreDocumentContent, setRestoreDocumentContent] = useState<any>(null);

  const canPublishUnlisted: boolean = !!schemas.uiSchema?.["ui:options"]?.["canPublishUnlisted"];

  const indexedDCompositeId = useMemo(
    () =>
      createCompositeId(
        documentToEdit?.id?.toString() || "new",
        state.user?.id || 0,
        schemas.schema_id || 0,
        documentToEdit?.owner_id || state?.cmsContextInstance?.owner_id || 0,
        state?.cmsContextInstance?.id || 0
      ),
    [
      documentToEdit?.id,
      state.user?.id,
      schemas.schema_id,
      documentToEdit?.owner_id,
      state?.cmsContextInstance?.owner_id,
      state?.cmsContextInstance?.id,
    ]
  );

  const handleDeleteTempStorage = useCallback(
    () =>
      deleteDocumentFromIndexedDB(
        documentToEdit?.id?.toString() || "new",
        state.user?.id || 0,
        schemas.schema_id || 0,
        documentToEdit?.owner_id || state?.cmsContextInstance?.owner_id || 0,
        state?.cmsContextInstance?.id || 0
      ),
    [
      documentToEdit?.id,
      state.user?.id,
      schemas.schema_id,
      documentToEdit?.owner_id,
      state?.cmsContextInstance?.owner_id,
      state?.cmsContextInstance?.id,
    ]
  );

  const resetRestoreDocumentStatus = {
    restoreDocument: null,
    documentToRestore: false,
    documentCompositeId: undefined,
  };

  usePrompt(
    t("generic.unsavedChangesMessage"),
    ({ currentLocation, nextLocation }) => {
      const isPathChanged = currentLocation.pathname !== nextLocation.pathname;
      setGeneratedData({ ...state.generatedData, showGeneratedFeedback: false, doGenerate: false });
      return isPathChanged
        ? isDirty ||
            (state.restoreDocumentStatus.documentCompositeId === indexedDCompositeId &&
              state.restoreDocumentStatus.documentToRestore === true)
        : false;
    },
    {
      //Options
      onApprove: () => {
        handleDeleteTempStorage();
        setRestoreDocumentStatus(resetRestoreDocumentStatus);
      },
    }
  );

  useEffect(() => documentToEdit?.content && setModifiedContent(documentToEdit.content), [documentToEdit]);

  const handleHeaderSubmitClick = (selectedOption?: string) => {
    switch (selectedOption) {
      case "generic.save_draft":
        handleSaveDocument(modifiedContent, DocumentStatus.DRAFT).then((res: any) => {
          setModifiedContent(res);
          handleDeleteTempStorage();
        });
        setSelectedDocumentStatus(DocumentStatus.DRAFT);
        break;
      case "generic.save_published":
        handleSaveDocument(modifiedContent, DocumentStatus.PUBLISHED).then((res: any) => {
          setModifiedContent(res);
          handleDeleteTempStorage();
        });
        setSelectedDocumentStatus(DocumentStatus.PUBLISHED);
        break;
      case "generic.save_published_not_listed":
        handleSaveDocument(modifiedContent, DocumentStatus.PUBLISHED_NOT_LISTED).then((res: any) => {
          setModifiedContent(res);
          handleDeleteTempStorage();
        });
        setSelectedDocumentStatus(DocumentStatus.PUBLISHED_NOT_LISTED);
        break;
      default:
        handleSaveDocument(modifiedContent, selectedDocumentStatus).then((res: any) => {
          setModifiedContent(res);
          handleDeleteTempStorage();
        });
    }

    if (!!documentToEdit?.id)
      setForceUpdateLocalStorage("forceUpdate", { type: "document", documentId: documentToEdit.id.toString() });

    setIsDirty(false);
  };

  const handleOnChange = (formData: any) => {
    if (!userHasDoneChanges()) {
      setIsDirty(false);
      setModifiedContent(formData);
      return;
    } else {
      addDocumentToIndexedDB({
        documentId: documentToEdit?.id?.toString() || "new",
        content: formData,
        schemaId: schemas.schema_id || 0,
        userId: state.user?.id || 0,
        ownerId: documentToEdit?.owner_id || state?.cmsContextInstance?.owner_id || 0,
        instanceId: state?.cmsContextInstance?.id || 0,
      });
      if (!formData || !documentToEdit?.content) {
        setIsDirty(true);
        setModifiedContent(formData || {});
        return;
      }
      if (!deepCompareFormData(formData, documentToEdit?.content)) {
        setIsDirty(true);
      } else {
        setIsDirty(false);
      }
      setModifiedContent(formData);
    }
  };

  const [currentClipboardItem, setCurrentClipboardItem] = useState<BlockClipboard | null>(null);
  const copy = useCallback<BlockClipboardContextValue["copy"]>((value) => {
    setCurrentClipboardItem({ value });
  }, []);
  const paste = useCallback<BlockClipboardContextValue["paste"]>(
    (pasteIntoArrayId$) => {
      // assumes that pasteIntoArrayId$ is a string in the format of
      // "formId-prefix_field1_field2_field3ThatIsABlockArray"
      if (currentClipboardItem?.value) {
        const targetPath = pasteIntoArrayId$.split("_").slice(1);
        const pasteValue = JSON.parse(JSON.stringify(currentClipboardItem.value));
        const newFormData = mergeObjects(
          JSON.parse(JSON.stringify(modifiedContent)),
          objectFromPath(targetPath, [pasteValue]),
          true
        );
        handleOnChange(newFormData);
      }
    },
    [currentClipboardItem, modifiedContent]
  );
  const clipboardContextValue: BlockClipboardContextValue = useMemo(
    () => ({
      currentClipboard: currentClipboardItem,
      copy,
      paste,
    }),
    [currentClipboardItem, copy, paste]
  );

  /**
   * @description Returns an object where keys are available locale ids and values are true if a locale has content.
   * Used to update language selector checkboxes.
   * Runs once for each rerender. Will work as long as modifiedContent is set by useState.
   * Avoid useMemo as it will cause extra rerenders.
   * @returns {object} - object where keys are available locale ids and values are true if a locale has content
   *
   **/
  const getAvailableLocalesWithContent = () => {
    const _availableLocalesWithContent = findDeepPropertiesWithContent(
      modifiedContent,
      availableLocales.map((al) => al.language_code)
    );
    const availableLocaleIdsWithContent: { [key: number]: boolean } = {};
    availableLocales.forEach((al) => {
      availableLocaleIdsWithContent[al.id!] = _availableLocalesWithContent[al.language_code];
    });

    return availableLocaleIdsWithContent;
  };
  const availableLocalesWithContent = getAvailableLocalesWithContent();

  useEffect(() => {
    if (isDirty) {
      localStorage.setItem("edited", JSON.stringify(isDirty));
    } else {
      localStorage.removeItem("edited");
    }
  }, [isDirty]);

  useEffect(() => {
    //Show modal if there is temp content for this document
    if (tempStorageDB === null || !schemas.schema_id || state.user === null || state.cmsContextInstance === null)
      return; // Wait until dependencies are ready

    getDocumentFromIndexedDB(
      documentToEdit?.id?.toString() || "new",
      state.user.id || 0,
      schemas.schema_id,
      documentToEdit?.owner_id || state.cmsContextInstance.owner_id || 0,
      state.cmsContextInstance.id || 0,
      documentToEdit?.updated_at
    ).then((res) => {
      if (res && res.content) {
        if (!deepCompareFormData(res.content, documentToEdit?.content ?? {})) {
          setRestoreDocumentStatus({
            ...state.restoreDocumentStatus,
            documentToRestore: true,
            documentCompositeId: res.id,
          });
          setRestoreDocumentContent(res.content);
        } else {
          // If content is the same as in DB, delete temp content
          handleDeleteTempStorage();
        }
      }
    });
  }, [tempStorageDB, documentToEdit, schemas, state.user, state.cmsContextInstance]);

  useEffect(() => {
    if (tempStorageDB === null) return; // Wait until tempStorageDB is ready

    if (state.restoreDocumentStatus.documentCompositeId === indexedDCompositeId) {
      if (state.restoreDocumentStatus.restoreDocument === true) {
        handleOnChange(restoreDocumentContent);
        setRestoreDocumentStatus(resetRestoreDocumentStatus);
      } else if (state.restoreDocumentStatus.restoreDocument === false) {
        handleDeleteTempStorage();
        setRestoreDocumentContent(null);
        setRestoreDocumentStatus(resetRestoreDocumentStatus);
      }
    }
  }, [tempStorageDB, state.restoreDocumentStatus.restoreDocument]);

  useEffect(() => {
    setGeneratedData({ ...state.generatedData, documentId: documentToEdit?.id });
  }, [documentToEdit?.id]);

  return (
    <Layout
      header={
        <EditorHeader
          disablePreview={disablePreview}
          previewOpen={!disablePreview && previewOpen}
          onPreviewOpenChanged={setPreviewOpen}
          onSubmit={handleHeaderSubmitClick}
          formIsDirty={isDirty}
          currentStatus={selectedDocumentStatus}
          lastModified={documentToEdit?.updated_at}
          lastModifiedBy={documentToEdit?.updated_by}
          setSelectedLocale={setSelectedLocale}
          selectedLocale={selectedLocale}
          canPublishUnlisted={canPublishUnlisted}
          modalEditor={modalEditor}
          availableLocalesWithContent={availableLocalesWithContent}
        />
      }
    >
      {!schemas.schema_id && !documentToEdit ? (
        <ErrorText>{t("views.admin.schemaPreview.missingSchema")}</ErrorText>
      ) : (
        <MediaSelector
          open={mediaSelectorOpen}
          setOpen={setMediaSelectorOpen}
          width={modalEditor ? "calc(50vw - 16px)" : undefined}
        >
          {modalEditor ? (
            <PersistentDrawer
              open={!disablePreview && previewOpen && !mediaSelectorOpen}
              position="right"
              width={Styles.Dimensions.EDITOR_PREVIEW_WIDTH}
              drawerContent={
                state.generatedData?.showGeneratedFeedback ? (
                  <AiFeedbackView />
                ) : (
                  <EditorPreview
                    schemas={schemas}
                    formData={modifiedContent}
                    selectedLocale={selectedLocale}
                    availableLocales={state.cmsContextInstance!.available_locales}
                    documentStatus={selectedDocumentStatus}
                    documentId={documentToEdit?.id}
                    isInsideModal={modalEditor}
                  />
                )
              }
              isOpenedInsideModal
            >
              <BlockClipboardContext.Provider value={clipboardContextValue}>
                <KioForm
                  formData={modifiedContent}
                  selectedLocale={selectedLocale || Settings.DEFAULT_LOCALE}
                  onChange={handleOnChange}
                  onSubmit={() => {}} // submit from header
                  onReferencesChange={setReferences}
                  schema={schemas.jsonSchema}
                  uiSchema={schemas.uiSchema}
                  isOpenedInsideModal={modalEditor}
                  remoteClientId={documentToEdit?.remote_client_id}
                >
                  <button type="submit" hidden={true} />
                </KioForm>
              </BlockClipboardContext.Provider>
            </PersistentDrawer>
          ) : (
            <PersistentDrawer
              open={!disablePreview && previewOpen && !mediaSelectorOpen}
              position="right"
              width={Styles.Dimensions.EDITOR_PREVIEW_WIDTH}
              drawerContent={
                state.generatedData?.showGeneratedFeedback ? (
                  <AiFeedbackView />
                ) : (
                  <EditorPreview
                    schemas={schemas}
                    formData={modifiedContent}
                    selectedLocale={selectedLocale}
                    availableLocales={state.cmsContextInstance!.available_locales}
                    documentStatus={selectedDocumentStatus}
                    documentId={documentToEdit?.id}
                    isInsideModal={modalEditor}
                  />
                )
              }
            >
              <BlockClipboardContext.Provider value={clipboardContextValue}>
                <FormContainer>
                  <KioForm
                    formData={modifiedContent}
                    selectedLocale={selectedLocale || Settings.DEFAULT_LOCALE}
                    onChange={handleOnChange}
                    onSubmit={() => {}} // submit from header
                    onReferencesChange={setReferences}
                    schema={schemas.jsonSchema}
                    uiSchema={schemas.uiSchema}
                    isOpenedInsideModal={modalEditor}
                    remoteClientId={documentToEdit?.remote_client_id}
                  >
                    <button type="submit" hidden={true} />
                  </KioForm>
                </FormContainer>
              </BlockClipboardContext.Provider>
            </PersistentDrawer>
          )}
        </MediaSelector>
      )}
    </Layout>
  );
};

export default EditDocumentView;
